import { HttpClient } from '@angular/common/http';
import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Platform } from '@app/classes';
import { AnalyticEvents, FeatureFlag } from '@app/enums';
import { RoleAssignment } from '@app/models/account/role-assignment.model';
import { BenefitDivision } from '@app/models/benefits/benefit-division.model';
import { Module } from '@app/models/common/module.model';
import { DocumentAssignment } from '@app/models/documents/document-assignment.model';
import { Candidate } from '@app/models/offer-letters/candidate.model';
import { TaskTemplate } from '@app/models/tasks/task-template.model';
import { Project } from '@app/models/time-tracking/project.model';
import { TrainingProgram } from '@app/models/training/training-program.model';
import { SimplyBenefitsOnboardingData } from '@app/modules/benefits/interfaces/simply-benefits-onboarding-data.interface';
import { SimplyBenefitsOnboardingEmployee } from '@app/modules/benefits/interfaces/simply-benefits-onboarding-employee.interface';
import { SimplyBenefitsService } from '@app/modules/benefits/services';
import { DocumentAssignmentService } from '@app/modules/documents-v2/services/document-assignment.service';
import { AssignTaskTemplatesService, EmployeeHiringService } from '@app/modules/employees/services';
import { TimeTrackingPayrollEmployeeIntegrationService } from '@app/modules/time-tracking/services/time-tracking-payroll-employee-integration.service';
import { AbilityService, AnalyticService, EmployeeModuleAssignmentService } from '@app/services';
import { AuthService } from '@app/services/auth.service';
import { FeatureService } from '@app/services/feature.service';
import { NotifyService } from '@app/services/notify.service';
import { StoreView } from '@common/views';
import { EmployeesResources } from '@employees/employees.resources';
import { SelectedPolicy } from '@forms/employees/components/onboarding-time-off-policies/onboarding-time-off-policies.form';
import { Account } from '@models/account/account.model';
import { Role } from '@models/account/role.model';
import { BenefitPlan } from '@models/benefits/benefit-plan.model';
import { BenefitPlan as EmployeeBenefitPlan } from '@models/employee/benefits/benefit-plan.model';
import { Employee } from '@models/employee/employee.model';
import { OnboardingOption } from '@models/employee/onboarding-option.model';
import { OnboardingReminder } from '@models/employee/onboarding-reminder.model';
import { OnboardingVideo } from '@models/employee/onboarding-video.model';
import { Salary } from '@models/employee/salary.model';
import { DataField } from '@models/settings/data-field.model';
import { WorkSchedule } from '@models/time-off-v3/works-schedule.model';
import * as Sentry from '@sentry/browser';
import { TimeOffService } from '@time-off-v3/services';
import { ProjectEmployeeAssignmentService } from '@time-tracking/services';
import moment from 'moment';

@Component({
    templateUrl: './employee-store.template.html',
    styleUrls: ['./employee-store.style.scss'],
})
export class EmployeeStoreView extends StoreView implements OnInit {
    isLoading = false;

    account: Account;
    employee: Employee;
    salary: Salary;

    documentAssignments: DocumentAssignment[] = [];
    reminders: OnboardingReminder[] = [];
    workSchedule: WorkSchedule | null = null;
    options: OnboardingOption;
    customFields: DataField[] = [];
    taskTemplates: TaskTemplate[] = [];
    timeOffPolicies: SelectedPolicy[] = [];
    roles: Role[] = [];
    trainingPrograms: TrainingProgram[] = [];

    benefitPlan: BenefitPlan | null = null;
    benefitDivision: BenefitDivision | null = null;
    isWaitingPeriodWaved = false;
    simplyBenefitsData: SimplyBenefitsOnboardingData | null = null;
    selectedBenefitLabel: string | null = null;

    addToTimeTracking = false;
    timeTrackingProjects: Project[] = [];
    addToTimeTrackingPayrollIntegration = false;

    onboardingVideo = new OnboardingVideo();
    fromURL = '';
    payrollEnabled = false;
    hasOnboardingVideoFeatureFlag = false;

    private employeeRole: Role | null = null;

    private candidate: Candidate | null = null;

    private hasEmployeeBeenCreated = false;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private auth: AuthService,
        private notify: NotifyService,
        private http: HttpClient,
        private hiringService: EmployeeHiringService,
        private timeOffService: TimeOffService,
        private documentAssignmentService: DocumentAssignmentService,
        private analyticsService: AnalyticService,
        private assignTasksService: AssignTaskTemplatesService,
        private featureService: FeatureService,
        private abilities: AbilityService,
        private simplyBenefitsService: SimplyBenefitsService,
        private projectEmployeeAssignmentService: ProjectEmployeeAssignmentService,
        private employeeModuleAssignmentService: EmployeeModuleAssignmentService,
        private timeTrackingPayrollEmployeeIntegrationService: TimeTrackingPayrollEmployeeIntegrationService
    ) {
        super();

        this.account = new Account();
        this.employee = new Employee();
        this.employee.status = 'onboarding';
        this.employee.collectHiringQuestions = false;
        this.salary = new Salary();
        this.payrollEnabled = this.abilities.payroll();
    }

    @HostListener('window:beforeunload', ['$event'])
    warn($event: BeforeUnloadEvent): void {
        if (!this.hasEmployeeBeenCreated) {
            const warningMessage = 'Are you sure you want to leave this page? Employee creation is still in progress.';
            $event.returnValue = warningMessage;
        }
    }

    async ngOnInit(): Promise<void> {
        this.hasOnboardingVideoFeatureFlag = await this.featureService.has(FeatureFlag.onboardingVideo);

        if (this.hasOnboardingVideoFeatureFlag) {
            const [options] = await OnboardingOption.param('company', this.auth.company.id)
                .with(['defaultOnboardingVideo'])
                .all();

            this.options = options[0];

            this.initOnboardingVideo();
        }

        this.populateFromParams();
        this.setDefaultRoleSelection();
        this.isLoading = false;
    }

    /**
     * onSave - called when the user clicks the 'Save' button.
     * This method is responsible for creating the employee, account, salary, and all other related models.
     * This is done through a series of promises, each of which is responsible for creating a specific model.
     * If any of the promises fail, the entire process is aborted, but any models that were created successfully still exist.
     * Obviously, this is not ideal, but it's the best we can do with the current architecture.
     * We also use Sentry tags to inform us of errors that occur during this process.
     */
    onSave(): void {
        if (this.isLoading) {
            return;
        }

        if (!this.roles.length) {
            // todo: inline validation before next step
            this.notify.error('applicant-tracker.employeeMustHaveAtLeastOneRoleAssigned');
            return;
        }

        this.isLoading = true;
        this.createAccount()
            .then(() => this.createEmployee())
            .then(() => {
                // If a current candidate is being hired, update 'employeeId' & 'hiredAt' on candidate model.
                if (this.candidate) {
                    return this.updateCandidateOnEmployeeCreation();
                }
            })
            .then(() => this.createSalary())
            .then(() => this.attachAll())
            .then(() => this.sendInvitations())
            .then(() => this.sendEvents())
            .then(() => {
                this.hasEmployeeBeenCreated = true;

                if (this.fromURL === 'recruiting') {
                    this.analyticsService.trackEvent(
                        AnalyticEvents.RecruitingApplicantProfileMeatballHireClickCompleteToSave
                    );
                }

                this.notify.success('applicant-tracker.anInvitationHasBeenSent');

                this.analyticsService.trackEvent(AnalyticEvents.CreateEmployeeClickComplete);
                if (this.simplyBenefitsData?.class) {
                    //simplyBenefitsData was successfuly saved then:
                    this.analyticsService.trackEvent(AnalyticEvents.SimplyBenefitsOnboardingEmployeeAssignClass);
                }
                this.trackOptionalSinEvents();

                this.router.navigate(['/employees', 'directory', this.employee.id, 'personal']);
            })
            .catch((err) => {
                this.isLoading = false;

                /*
                 * We want to capture any errors that happen here so we can address them
                 * since usually an error here will result in an account being made.
                 * This results in admins getting stuck since an account
                 * already exists for a given email address.
                 */
                Sentry.captureException(err, {
                    tags: {
                        event: 'new-employee-creation',
                    },
                });

                this.notify.error('applicant-tracker.unableToAddEmployee');
            });
    }

    trackOptionalSinEvents(): void {
        // HR Only Cases
        if (!this.payrollEnabled && !this.employee.isSinRequired) {
            this.analyticsService.trackEvent(AnalyticEvents.EmployeeCreatedWithOptionalSIN);
            return;
        }
        if (!this.payrollEnabled && this.employee.isSinRequired) {
            this.analyticsService.trackEvent(AnalyticEvents.EmployeeCreatedWithRequiredSIN);
            return;
        }

        // With Payroll Module Cases
        if (this.payrollEnabled && !this.employee.isSinRequired) {
            this.analyticsService.trackEvent(AnalyticEvents.EmployeeCreatedWithNotOnPayrollAndOptionalSIN);
            return;
        }
        if (this.payrollEnabled && this.employee.isSinRequired) {
            this.analyticsService.trackEvent(AnalyticEvents.EmployeeCreatedWithOnPayrollAndRequiredSIN);
            return;
        }
    }

    onStepChange(): void {
        this.hiringService.account = this.account;
        this.hiringService.employee = this.employee;
        this.hiringService.syncEmployeeName();
        this.hiringService.salary = this.salary;
    }

    private async createAccount(): Promise<void> {
        const account = await this.account.save();
        this.account = account;
    }

    private async createEmployee(): Promise<void> {
        this.employee.accountId = this.account.id;
        this.employee.firstName = this.account.legalFirstName;
        this.employee.lastName = this.account.legalLastName;
        this.employee.workScheduleId = this.workSchedule?.id ?? null;

        const employee = await this.employee.param('company', this.auth.company.id).save();
        this.employee = employee;
    }

    private async createSalary(): Promise<void> {
        this.salary.employeeId = this.employee.id;
        this.salary.effectiveAt = moment(this.employee.hiredAt).toDate();
        this.salary.isPrimary = true;
        this.salary.name = 'Regular Pay';

        const salary = await this.salary
            .param('company', this.auth.company.id)
            .param('employee', this.employee.id)
            .save();
        this.salary = salary;
    }

    /**
     * Creates the connection between the new employee and the
     * entities they've been assigned.
     */
    private attachAll(): Promise<unknown> {
        return Promise.all([
            this.attachRole(),
            this.attachReminders(),
            this.attachCustomFields(),
            this.assignTasks(),
            this.assignDocuments(),
            this.attachTimeOffPolicies(),
            this.attachBenefitPlan(),
            this.attachTrainingPrograms(),
            this.attachOnboardingVideo(),
            this.attachTimeTracking(),
        ]);
    }

    private attachTrainingPrograms(): Promise<unknown> {
        return Promise.all(
            this.trainingPrograms.map((trainingProgram: TrainingProgram) => {
                trainingProgram.attach('employees', this.employee.id);

                return trainingProgram.param('company', this.auth.company.id).save();
            })
        );
    }

    private attachRole(): Promise<unknown> {
        let roleIds = this.roles.length ? this.roles.map((role) => role.id) : [this.employeeRole?.id];
        // ensure we have no null values here
        roleIds = roleIds.filter(Boolean);

        const promises = roleIds.map((roleId) =>
            new RoleAssignment({ roleId: roleId }).param('account', this.account.id).save()
        );

        return Promise.all(promises);
    }

    /**
     * Assign documents to the employee
     *
     * Handles 3 cases differently:
     *
     * 1. Document Acknowledgements are done synchronously by updating the CompanyDocument
     * 2. Edited documents (template modified during onboarding) use SignatureRequest Store endpoint
     * 3. All other documents (single/multi order/unordered) use the createSignatureRequests endpoint
     */
    private async assignDocuments(): Promise<void> {
        const signingDocumentAssignments = this.documentAssignments.filter((d) => d.isSigningDocument());
        const documentAcknowledgementAssignments = this.documentAssignments.filter((d) =>
            d.isDocumentAcknowledgement()
        );

        this.documentAssignmentService.attachDocumentAcknowledgements(
            documentAcknowledgementAssignments,
            this.employee
        );

        for (const signingDocumentAssignment of signingDocumentAssignments) {
            try {
                if (signingDocumentAssignment.hasTemplateBeenEdited()) {
                    await this.documentAssignmentService.createSignatureRequestForEditedRequest(
                        signingDocumentAssignment,
                        this.employee
                    );
                    continue;
                }

                await Promise.all(this.documentAssignmentService.updateSlotAssignments(signingDocumentAssignment));
                await this.documentAssignmentService.createSignatureRequests(signingDocumentAssignment, true);
            } catch (err) {
                this.notify.error({
                    key: 'applicant-tracker.errorAssigningDocumentName',
                    params: { name: signingDocumentAssignment.companyDocument.name },
                });
            }
        }
    }

    private async assignTasks(): Promise<void> {
        if (!this.taskTemplates.length) {
            return;
        }

        this.isLoading = true;
        try {
            await this.assignTasksService.onSave(this.taskTemplates, [this.employee]);
            this.analyticsService.trackEvent(AnalyticEvents.AddTasksDuringNewHireFlow);
        } catch (err) {
            this.notify.error(err);
        }
    }

    private attachCustomFields(): Promise<any> {
        return Promise.all(
            this.customFields.map((cField: DataField) => {
                cField.attach('employees', this.employee.id);

                return cField.param('company', this.auth.company.id).save();
            })
        );
    }

    private attachReminders(): Promise<any> {
        if (this.reminders.length === 0) {
            return Promise.resolve();
        }
        const reminderIds = this.reminders.map((r: OnboardingReminder) => r.id);
        const url: string = EmployeesResources.AssignReminders.replace(':company', this.auth.company.id.toString());

        return this.http
            .post(url, {
                data: {
                    type: 'reminders',
                    attributes: {
                        onboardingReminderIds: reminderIds,
                        employeeId: this.employee.id,
                    },
                },
            })
            .toPromise();
    }

    private attachBenefitPlan(): Promise<EmployeeBenefitPlan | SimplyBenefitsOnboardingEmployee | void> {
        if (this.simplyBenefitsData) {
            return this.simplyBenefitsService.setOnboardingDataOfEmployee(this.employee.id, this.simplyBenefitsData);
        }

        if (this.benefitPlan === null) {
            return Promise.resolve();
        }
        const employeeBenefitPlan = new EmployeeBenefitPlan({
            employeeId: this.employee.id,
            benefitPlanId: this.benefitPlan.id,
            benefitDivisionId: this.benefitDivision?.id,
            isWaitingPeriodWaved: this.isWaitingPeriodWaved,
        });
        return employeeBenefitPlan.param('employee', this.employee.id).save();
    }

    private sendInvitations(): Promise<any> {
        return this.auth.sendInvitations([this.account.id], this.auth.company.id);
    }

    private populateFromParams(): void {
        const queryParams = this.route.snapshot.queryParams;

        this.account.email = queryParams.email || null;
        this.account.legalFirstName = queryParams.firstName || queryParams.first_name || null;
        this.account.legalLastName = queryParams.lastName || queryParams.last_name || null;
        this.employee.atsApplicantId = queryParams.atsApplicantId || null;
        this.employee.firstName = queryParams.firstName || queryParams.first_name || null;
        this.employee.lastName = queryParams.lastName || queryParams.last_name || null;
        this.salary.rate = queryParams.salary || null;
        this.employee.hiredAt = queryParams.hireDate || queryParams.hire_date || null;
        this.employee.positionId = queryParams.jobId || queryParams.job_id || null;
        this.employee.departmentId = queryParams.departmentId || queryParams.department_id || null;
        this.employee.officeId = queryParams.addressId || queryParams.address_id || null;
        this.employee.offerAcceptedDate = queryParams.offerLetterSignedAt || null;
        this.fromURL = queryParams.fromURL || queryParams.from_url || null;

        if (!queryParams.candidateId) {
            return;
        }

        this.populateBasedOnCandidateId(queryParams.candidateId);
    }

    private async populateBasedOnCandidateId(candidateId: number): Promise<void> {
        try {
            this.candidate = await Candidate.with([
                'latestOfferLetter',
                'latestOfferLetter.department',
                'latestOfferLetter.location',
                'latestOfferLetter.position',
            ]).find(candidateId);

            if (!this.candidate) {
                return;
            }

            this.account.email = this.candidate.email;
            this.account.legalFirstName = this.candidate.legalFirstName;
            this.account.legalLastName = this.candidate.legalLastName;
            this.employee.firstName = this.candidate.legalFirstName;
            this.employee.lastName = this.candidate.legalLastName;
            this.employee.manager = this.candidate.latestOfferLetter.reportsTo;
            this.employee.offerLetterId = this.candidate?.latestOfferLetter?.id;

            this.salary.rate = this.candidate.latestOfferLetter.compensationRate;
            this.salary.frequency = this.candidate.latestOfferLetter.compensationFrequency;
            this.salary.hoursPerWeek = this.candidate.latestOfferLetter.hoursPerWeek || 40;
            this.employee.stockOptions = this.candidate.latestOfferLetter.stockOptions;
            this.employee.employmentType = this.candidate.latestOfferLetter.employmentType;

            this.employee.position = this.candidate.latestOfferLetter.position;
            this.employee.department = this.candidate.latestOfferLetter.department;
            this.employee.office = this.candidate.latestOfferLetter.location;
        } catch (err) {
            this.notify.error(err.message || 'applicant-tracker.candidateCouldNotBeFound');
        }
    }

    private async updateCandidateOnEmployeeCreation(): Promise<void> {
        if (!this.candidate) {
            return;
        }

        this.candidate.employeeId = this.employee.id;
        this.candidate.hiredAt = this.employee.hiredAt;

        try {
            await this.candidate.save();
        } catch (err) {
            this.notify.error(err.message || 'applicant-tracker.couldNotUpdateCandidate');
        }
    }

    private setDefaultRoleSelection(): void {
        Role.where('company_id', this.auth.company.id)
            .where('assignableBy', this.auth.role.id)
            .all()
            .then(([roles]) => {
                // its possible this role does not have the Assignment Control for the Default Employee (or any role)
                this.employeeRole = roles.find((role) => role.name === 'Employee' && role.isImmutable) ?? null;
                if (this.employeeRole) {
                    this.roles = [this.employeeRole];
                }
            });
    }

    private attachTimeOffPolicies(): void {
        if (this.timeOffPolicies?.filter((selectedPolicy) => !!selectedPolicy?.timeOffPolicy).length > 0) {
            this.analyticsService.trackEvent(AnalyticEvents.PeopleCreatingNewHireAssignTimeOffPolicy);
        }

        this.timeOffPolicies
            .filter((selectedPolicy) => !!selectedPolicy?.timeOffPolicy)
            .forEach(async (selectedPolicy) => {
                await this.timeOffService.assignAndUnassignEmployeesToPolicy(selectedPolicy.timeOffPolicy.id, {
                    employees: [{ id: this.employee.id, effectiveAt: selectedPolicy.effectiveAt }],
                });
            });
    }

    private async initOnboardingVideo(): Promise<void> {
        this.onboardingVideo = new OnboardingVideo({
            video: this.options?.defaultOnboardingVideo,
        });
    }

    private async attachOnboardingVideo(): Promise<void> {
        if (!this.hasOnboardingVideoFeatureFlag) {
            return;
        }

        if (!this.onboardingVideo.video) {
            return;
        }

        // If the selected onboarding video is the same as the default onboarding video,
        // use the existing video.
        if (this.onboardingVideo.video?.url === this.options?.defaultOnboardingVideo?.url) {
            this.onboardingVideo.video = this.options.defaultOnboardingVideo;
        }
        // Otherwise, save the video.
        else {
            await this.onboardingVideo.video.save();
            this.onboardingVideo.videoId = this.onboardingVideo.video.id;
        }

        this.onboardingVideo.employee = this.employee;

        await this.onboardingVideo.save();
    }

    private async attachTimeTracking(): Promise<void> {
        if (!this.addToTimeTracking) {
            return;
        }

        try {
            const timeTrackingModule = this.auth.company?.modules.find(
                (module) => module.name === Platform.modules.timeTracking
            ) as Module;

            await this.employeeModuleAssignmentService.assign([
                { employeeId: this.employee.id, moduleId: timeTrackingModule.id },
            ]);

            await this.projectEmployeeAssignmentService.assign(
                this.projectEmployeeAssignmentService.createForEmployee(this.employee, this.timeTrackingProjects)
            );

            if (this.addToTimeTrackingPayrollIntegration && this.employee.isPayrollSyncEnabled) {
                await this.timeTrackingPayrollEmployeeIntegrationService.batch([this.employee], []);
            }
        } catch (err) {
            // We'll notify the user of an error, and let Sentry know, but we won't block the rest of the flow
            this.notify.error('time-tracking.assignToTimeTrackingError');
            Sentry.captureException(err, {
                tags: {
                    event: 'new-employee-creation',
                },
            });
        }
    }

    /**
     * Send any events after employee is successfully created
     */
    private sendEvents(): void {
        if (this.candidate) {
            this.analyticsService.trackEvent(AnalyticEvents.OfferLetterHireCandidate);
        }
    }
}
